import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { findAnchor, useTourControls } from '../hooks/TourContext';
import type { TourStep } from '../hooks/TourContext';
import { tx } from '../i18n';

/**
 * The guided tour as it is drawn: one element lit, everything else dimmed, and a card beside it.
 *
 * <p>The dim is four rectangles around the element rather than one translucent sheet with a hole
 * punched in it. A sheet needs a mask or a blend mode to make a hole, and both of those change how
 * the colours underneath render - on the one screen whose whole purpose is showing the reader what
 * something really looks like. Four rectangles leave the element untouched, pixel for pixel.
 *
 * <p>The card is placed from the element's rectangle alone, never from its own measured height: a
 * layout that measures itself and then moves is a layout that can oscillate. It goes on whichever
 * side has more room, is given that room as a {@code max-height} with its own scrollbar, and falls
 * back to a sheet along the bottom when the viewport is too narrow or too shallow for either side.
 */

/** Below this, there is no room beside anything; the card becomes a sheet at the bottom. */
const NARROW = 640;
/** The card's width on a wide screen. Fixed, so the horizontal clamp needs no measurement. */
const CARD_WIDTH = 360;
/** Between the element and the card. */
const GAP = 12;
/** The smallest edge the card is ever pushed to. */
const GUTTER = 16;
/** A side with less room than this is not worth using; the sheet is better. */
const MIN_ROOM = 160;
/** How far the lit area extends past the element, so a focus ring is not clipped by the dim. */
const PAD = 6;

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** The element's rectangle, grown by {@link PAD} and clipped to the viewport. */
function litArea(rect: DOMRect): Box {
  const top = Math.max(0, rect.top - PAD);
  const left = Math.max(0, rect.left - PAD);
  const bottom = Math.min(window.innerHeight, rect.bottom + PAD);
  const right = Math.min(window.innerWidth, rect.right + PAD);
  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

function dimStyles(box: Box): Array<{ key: string; style: CSSProperties }> {
  const bottom = box.top + box.height;
  const right = box.left + box.width;
  return [
    { key: 'above', style: { top: 0, left: 0, width: '100%', height: box.top } },
    {
      key: 'below',
      style: { top: bottom, left: 0, width: '100%', height: Math.max(0, window.innerHeight - bottom) },
    },
    { key: 'left', style: { top: box.top, left: 0, width: box.left, height: box.height } },
    {
      key: 'right',
      style: { top: box.top, left: right, width: Math.max(0, window.innerWidth - right), height: box.height },
    },
  ];
}

function cardStyle(box: Box): CSSProperties {
  const roomBelow = window.innerHeight - (box.top + box.height) - GAP - GUTTER;
  const roomAbove = box.top - GAP - GUTTER;
  const tooNarrow = window.innerWidth <= NARROW || window.innerWidth < CARD_WIDTH + GUTTER * 2;

  if (tooNarrow || Math.max(roomBelow, roomAbove) < MIN_ROOM) {
    return { left: GUTTER, right: GUTTER, bottom: GUTTER, maxHeight: '62vh' };
  }

  const left = Math.min(Math.max(GUTTER, box.left), window.innerWidth - CARD_WIDTH - GUTTER);
  if (roomBelow >= roomAbove) {
    return { top: box.top + box.height + GAP, left, width: CARD_WIDTH, maxHeight: roomBelow };
  }
  return { bottom: window.innerHeight - box.top + GAP, left, width: CARD_WIDTH, maxHeight: roomAbove };
}

export default function ProductTour() {
  const { running } = useTourControls();
  if (!running) {
    return null;
  }
  return <TourStage id={running.id} steps={running.steps} index={running.index} />;
}

function TourStage({ id, steps, index }: { id: string; steps: TourStep[]; index: number }) {
  const { next, back, stop } = useTourControls();
  const dialog = useRef<HTMLDivElement>(null);
  const origin = useRef<HTMLElement | null>(null);
  /** The step focus has already been moved for, so a re-measurement does not move it again. */
  const focused = useRef<number | null>(null);
  const [box, setBox] = useState<Box | null>(null);
  // The provider only ever opens a tour at a step it resolved, but the index is still an index.
  const step: TourStep | undefined = steps[index];
  const last = index === steps.length - 1;

  // Where focus was before the tour opened, so it can be handed back when the tour closes.
  useEffect(() => {
    origin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => origin.current?.focus();
  }, []);

  useLayoutEffect(() => {
    const element = step ? findAnchor(step.anchor) : null;
    if (!element) {
      // The page changed under the tour: skip to the next step rather than light up nothing.
      next();
      return undefined;
    }

    const rect = element.getBoundingClientRect();
    // Only scroll when it is not already comfortably in view: scrolling on every step jolts.
    if (rect.top < GUTTER || rect.bottom > window.innerHeight - MIN_ROOM) {
      element.scrollIntoView({ block: 'center', inline: 'nearest' });
    }

    let frame = 0;
    const measure = () => setBox(litArea(element.getBoundingClientRect()));
    measure();
    const remeasure = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    };
    // Capture phase, because the thing that scrolled may be a panel rather than the window.
    window.addEventListener('scroll', remeasure, true);
    window.addEventListener('resize', remeasure);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', remeasure, true);
      window.removeEventListener('resize', remeasure);
    };
  }, [step, next]);

  /*
   * Focus moves to the card once per step, so each new title and body is read out.
   *
   * It waits for the card to exist: the first render of a step has no measurement yet and draws
   * nothing, so focusing on mount alone focused nothing at all - and with focus still out in the
   * page, Escape and the arrow keys went to the page instead of the tour. The step is recorded so
   * that a later measurement, from a scroll or a resize, does not snatch focus back off a button
   * the reader had moved to.
   */
  useEffect(() => {
    if (box && focused.current !== index) {
      focused.current = index;
      dialog.current?.focus();
    }
  }, [box, index]);

  /*
   * Escape and the arrows are bound to the document, not to the card.
   *
   * A modal has to answer Escape wherever focus happens to be. Focus can leave the card for
   * reasons the tour does not control - a press on the dim, a browser that moves focus on scroll -
   * and a tour that can then only be closed with the mouse is a trap.
   */
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        stop();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        next();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        back();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [next, back, stop]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') {
      return;
    }
    // A modal keeps the keyboard inside it; there are only ever two or three buttons in here.
    const buttons = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button') ?? []);
    if (buttons.length === 0) {
      return;
    }
    const first = buttons[0];
    const lastButton = buttons[buttons.length - 1];
    if (!first || !lastButton) {
      return;
    }
    if (!event.shiftKey && document.activeElement === lastButton) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
      event.preventDefault();
      lastButton.focus();
    }
  };

  if (!box || !step) {
    return null;
  }

  const titleId = `tour-${id}-title`;
  const bodyId = `tour-${id}-body`;

  return (
    <div className="tour">
      {dimStyles(box).map((part) => (
        <div key={part.key} className="tour__dim" style={part.style} aria-hidden="true" />
      ))}
      {/* Over the lit element, so the tour is the only thing that can be pressed while it runs. */}
      <div
        className="tour__block"
        style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
        aria-hidden="true"
      />
      <div
        className="tour__ring"
        style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
        aria-hidden="true"
      />

      <div
        className="tour__card"
        style={cardStyle(box)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        ref={dialog}
        onKeyDown={onKeyDown}
      >
        <p className="tour__count small muted">
          {tx(
            `Step ${index + 1} of ${steps.length}`,
            `Adım ${index + 1} / ${steps.length}`,
            `Krok ${index + 1} z ${steps.length}`,
            `Passo ${index + 1} de ${steps.length}`,
            `Paso ${index + 1} de ${steps.length}`,
          )}
        </p>
        <h2 className="tour__title" id={titleId}>
          {step.title}
        </h2>
        <p className="tour__body" id={bodyId}>
          {step.body}
        </p>
        <div className="tour__actions">
          <button type="button" className="button button--ghost button--small" onClick={stop}>
            {tx('Skip', 'Atla', 'Pomiń', 'Ignorar', 'Omitir')}
          </button>
          <span className="tour__spacer" />
          {index > 0 ? (
            <button type="button" className="button button--secondary button--small" onClick={back}>
              {tx('Back', 'Geri', 'Wstecz', 'Anterior', 'Atrás')}
            </button>
          ) : null}
          <button type="button" className="button button--small" onClick={next}>
            {last
              ? tx('Done', 'Bitir', 'Gotowe', 'Concluir', 'Hecho')
              : tx('Next', 'İleri', 'Dalej', 'Seguinte', 'Siguiente')}
          </button>
        </div>
      </div>
    </div>
  );
}
